import {
  createSlice,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";
import { config } from "../../utils/config";

// Get business dashboard data with optional filter
export const getBusinessDashboard = createAsyncThunk(
  "businessOwner/getBusinessDashboard",
  async ({ filter = "THIS_MONTH", from = "", to = "" } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("filter", filter);
      if (filter === "CUSTOM" && from && to) {
        params.append("from", from);
        params.append("to", to);
      }
      const query = params.toString();
      const url = `/dashboard/business-owner/reports/dashboard${query ? `?${query}` : ""}`;
      const res = await http.get(url);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch dashboard data"
      );
    }
  }
);

// Get business owner's business details
export const getBusinessDetails = createAsyncThunk(
  "businessOwner/getBusinessDetails",
  async (_, { rejectWithValue }) => {
    try {
      const res = await http.get("/dashboard/business-owner/business");
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch business details"
      );
    }
  }
);

// Update business owner's business details
export const updateBusinessDetails = createAsyncThunk(
  "businessOwner/updateBusinessDetails",
  async (data, { rejectWithValue }) => {
    try {
      const res = await http.put("/dashboard/business-owner/business", data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update business details"
      );
    }
  }
);

// Update business owner's discount
export const updateBusinessDiscount = createAsyncThunk(
  "businessOwner/updateBusinessDiscount",
  async (data, { rejectWithValue }) => {
    try {
      const res = await http.put("/dashboard/business-owner/business/discount", data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update discount"
      );
    }
  }
);

// Update business owner's gallery
export const updateBusinessGallery = createAsyncThunk(
  "businessOwner/updateBusinessGallery",
  async (data, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Add new gallery images
      if (data.gallery && data.gallery.length > 0) {
        data.gallery.forEach((file, index) => {
          if (file instanceof File) {
            formData.append(`gallery[${index}]`, file);
          } else if (file.file && !file.existing) {
            formData.append(`gallery[${index}]`, file.file);
          }
        });
      }

      // Add images to remove
      if (data.remove_images && data.remove_images.length > 0) {
        data.remove_images.forEach((imageUuid, index) => {
          formData.append(`remove_images[${index}]`, imageUuid);
        });
      }

      const res = await http.post("/dashboard/business-owner/business/gallery", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update gallery"
      );
    }
  }
);

// Set primary image
export const setPrimaryImage = createAsyncThunk(
  "businessOwner/setPrimaryImage",
  async (imageUuid, { rejectWithValue }) => {
    try {
      const res = await http.patch("/business-owner/business/gallery/set-primary", {
        image_uuid: imageUuid,
      });
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to set primary image"
      );
    }
  }
);

// Fetch services by category slug (public API)
export const fetchServicesBySlug = createAsyncThunk(
  "businessOwner/fetchServicesBySlug",
  async (slug, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/public/categories/${slug}/services`);
      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }
      const data = await response.json();
      return data;
    } catch (err) {
      return rejectWithValue(
        err.message || "Failed to fetch services"
      );
    }
  }
);

// Fetch payment modes for business owner
export const fetchBusinessOwnerPaymentModes = createAsyncThunk(
  "businessOwner/fetchPaymentModes",
  async (_, { rejectWithValue }) => {
    try {
      const res = await http.get("/dashboard/business-owner/payment-modes");
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch payment modes"
      );
    }
  }
);

// Get transaction summary report (paginated, filterable by year + month + date range)
export const getTransactionSummaryReport = createAsyncThunk(
  "businessOwner/getTransactionSummaryReport",
  async ({ year, month, from_date, to_date, per_page = 15, page = 1 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append("year", year);
      if (month) params.append("month", month);
      if (from_date) params.append("from_date", from_date);
      if (to_date) params.append("to_date", to_date);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const query = params.toString();
      const url = `/dashboard/business-owner/reports/transactions/summary${query ? `?${query}` : ""}`;
      const res = await http.get(url);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch transaction report"
      );
    }
  }
);
// business-owner/reports/transactions/summary

// Export report as XLSX – returns a blob URL for download
export const exportReportXlsx = createAsyncThunk(
  "businessOwner/exportReportXlsx",
  async ({ year, month, from_date, to_date } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams();
      if (year) params.append("year", year);
      if (month) params.append("month", month);
      if (from_date) params.append("from_date", from_date);
      if (to_date) params.append("to_date", to_date);
      const query = params.toString();
      const url = `${config.API_BASE_URL}/dashboard/business-owner/reports/transactions/export${query ? `?${query}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return { blobUrl, filename: `transactions_${year}_${month}.xlsx` };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to export XLSX");
    }
  }
);

// Export report as PDF – returns a blob URL for download
export const exportReportPdf = createAsyncThunk(
  "businessOwner/exportReportPdf",
  async ({ year, month, from_date, to_date } = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const params = new URLSearchParams();
      if (year) params.append("year", year);
      if (month) params.append("month", month);
      if (from_date) params.append("from_date", from_date);
      if (to_date) params.append("to_date", to_date);
      const query = params.toString();
      const url = `${config.API_BASE_URL}/dashboard/business-owner/reports/transactions/export-pdf${query ? `?${query}` : ""}`;

      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return { blobUrl, filename: `transactions_${year}_${month}.pdf` };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to export PDF");
    }
  }
);

/** Fetch paid months for business owner invoices */
export const getBusinessInvoicePaidMonths = createAsyncThunk(
  "businessOwner/getBusinessInvoicePaidMonths",
  async (_, { rejectWithValue }) => {
    try {
      const res = await http.get("/dashboard/business-owner/invoices/paid-months");
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch invoices");
    }
  }
);

/** Download invoice PDF for a specific month */
export const downloadBusinessInvoice = createAsyncThunk(
  "businessOwner/downloadBusinessInvoice",
  async ({ businessUuid, month, year, monthName }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const url = `${config.API_BASE_URL}/dashboard/business-owner/invoices/${businessUuid}/${month}/${year}/download`;

      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.message || "Invoice not available");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return { blobUrl, filename: `Invoice-${monthName}-${year}.pdf`, month, year };
    } catch (err) {
      return rejectWithValue(err.message || "Failed to download invoice");
    }
  }
);

const businessOwnerSlice = createSlice({
  name: "businessOwner",
  initialState: {
    business: null,
    dashboard: null,
    loading: false,
    dashboardLoading: false,
    error: null,
    dashboardError: null,
    updateStatus: "idle",
    updateError: null,
    services: [],
    servicesLoading: false,
    servicesError: null,
    paymentModes: [],
    paymentModesLoading: false,
    paymentModesError: null,
    // Transaction report
    report: null,
    reportLoading: false,
    reportError: null,
    exportLoading: false,
    exportError: null,
    // Invoices
    invoicePaidMonths: [],
    invoiceBusiness: null,
    invoiceLoading: false,
    invoiceError: null,
    invoiceDownloadingMap: {},
  },
  reducers: {
    resetUpdateStatus: (state) => {
      state.updateStatus = "idle";
      state.updateError = null;
    },
    resetServicesState: (state) => {
      state.services = [];
      state.servicesLoading = false;
      state.servicesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get dashboard data
      .addCase(getBusinessDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(getBusinessDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboard = action.payload.data;
      })
      .addCase(getBusinessDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })

      // Get business details
      .addCase(getBusinessDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBusinessDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.business = action.payload.data;
      })
      .addCase(getBusinessDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update business details
      .addCase(updateBusinessDetails.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateBusinessDetails.fulfilled, (state, action) => {
        state.updateStatus = "success";
        state.business = action.payload.data;
      })
      .addCase(updateBusinessDetails.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      })

      // Update discount
      .addCase(updateBusinessDiscount.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateBusinessDiscount.fulfilled, (state, action) => {
        state.updateStatus = "success";
        state.business = action.payload.data;
      })
      .addCase(updateBusinessDiscount.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      })

      // Update gallery
      .addCase(updateBusinessGallery.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(updateBusinessGallery.fulfilled, (state, action) => {
        state.updateStatus = "success";
        // Refresh business details after gallery update
      })
      .addCase(updateBusinessGallery.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      })

      // Set primary image
      .addCase(setPrimaryImage.pending, (state) => {
        state.updateStatus = "loading";
        state.updateError = null;
      })
      .addCase(setPrimaryImage.fulfilled, (state, action) => {
        state.updateStatus = "success";
      })
      .addCase(setPrimaryImage.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.updateError = action.payload;
      })

      // Fetch services by slug
      .addCase(fetchServicesBySlug.pending, (state) => {
        state.servicesLoading = true;
        state.servicesError = null;
      })
      .addCase(fetchServicesBySlug.fulfilled, (state, action) => {
        state.servicesLoading = false;
        state.services = action.payload.data || [];
      })
      .addCase(fetchServicesBySlug.rejected, (state, action) => {
        state.servicesLoading = false;
        state.servicesError = action.payload;
      })

      // Fetch payment modes for business owner
      .addCase(fetchBusinessOwnerPaymentModes.pending, (state) => {
        state.paymentModesLoading = true;
        state.paymentModesError = null;
      })
      .addCase(fetchBusinessOwnerPaymentModes.fulfilled, (state, action) => {
        state.paymentModesLoading = false;
        state.paymentModes = action.payload.data || [];
      })
      .addCase(fetchBusinessOwnerPaymentModes.rejected, (state, action) => {
        state.paymentModesLoading = false;
        state.paymentModesError = action.payload;
      })

      // Get transaction summary report
      .addCase(getTransactionSummaryReport.pending, (state) => {
        state.reportLoading = true;
        state.reportError = null;
      })
      .addCase(getTransactionSummaryReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        // Backend wraps response in { success, message, data: { filter, data, pagination, stats } }
        const responseData = action.payload?.data || action.payload;
        state.report = {
          data: responseData?.data || [],
          pagination: responseData?.pagination || null,
          filter: responseData?.filter || null,
          stats: responseData?.stats || null,
        };
      })
      .addCase(getTransactionSummaryReport.rejected, (state, action) => {
        state.reportLoading = false;
        state.reportError = action.payload;
        state.report = null;
      })

      // Export XLSX
      .addCase(exportReportXlsx.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportReportXlsx.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportReportXlsx.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload;
      })

      // Export PDF
      .addCase(exportReportPdf.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportReportPdf.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportReportPdf.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload;
      })

      // Get invoice paid months
      .addCase(getBusinessInvoicePaidMonths.pending, (state) => {
        state.invoiceLoading = true;
        state.invoiceError = null;
      })
      .addCase(getBusinessInvoicePaidMonths.fulfilled, (state, action) => {
        state.invoiceLoading = false;
        const payload = action.payload?.data || action.payload;
        state.invoicePaidMonths = payload?.paid_months || [];
        state.invoiceBusiness = payload?.business || null;
      })
      .addCase(getBusinessInvoicePaidMonths.rejected, (state, action) => {
        state.invoiceLoading = false;
        state.invoiceError = action.payload;
        state.invoicePaidMonths = [];
      })

      // Download invoice
      .addCase(downloadBusinessInvoice.pending, (state, action) => {
        const { month, year } = action.meta.arg;
        state.invoiceDownloadingMap[`${month}-${year}`] = true;
      })
      .addCase(downloadBusinessInvoice.fulfilled, (state, action) => {
        const { month, year } = action.payload;
        state.invoiceDownloadingMap[`${month}-${year}`] = false;
      })
      .addCase(downloadBusinessInvoice.rejected, (state, action) => {
        const { month, year } = action.meta.arg;
        state.invoiceDownloadingMap[`${month}-${year}`] = false;
      });
  },
});

export const { resetUpdateStatus, resetServicesState } = businessOwnerSlice.actions;
export default businessOwnerSlice.reducer;
